import { ChangeDetectionStrategy, Component, effect, ElementRef, inject, OnInit, signal, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';

import { Store } from '@ngrx/store';

import { selectUser } from '../../../../store/app.selectors';
import { AttachmentTypes, IAttachment } from '../../../../modules/chat/interfaces/IAttachment';
import { AppStateWithChat } from '../../../../modules/chat/store/chat.reducer';
import { IUserProfile } from '../../../../modules/user/interfaces/IUserProfile';
import { updateUser } from '../../../../modules/user/store/user.actions';

@Component({
  selector: 'ngx-profile-modal',
  templateUrl: './profile-modal.component.html',
  styleUrls: ['./profile-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ProfileModalComponent implements OnInit {
  private dialogRef = inject(MatDialogRef<ProfileModalComponent>);
  private fb = inject(FormBuilder);
  private store = inject(Store<AppStateWithChat>);

  user = toSignal(this.store.select(selectUser), { initialValue: null as IUserProfile | null });
  imgProfileUpload = viewChild<ElementRef<HTMLInputElement>>('imgProfileUpload');

  attachments = signal<IAttachment[]>([]);
  currentUserSesion = signal<IUserProfile | null>(null);
  profileImageURL = signal('');
  userForm: FormGroup = this.fb.group({
    displayName: ['', Validators.required],
  });

  constructor() {
    effect(() => {
      const user = this.user();
      if (!user) {
        return;
      }
      this.currentUserSesion.set({ ...user });
      this.setForm();
    });
  }

  ngOnInit(): void {
    const user = this.user();
    if (user) {
      this.currentUserSesion.set({ ...user });
      this.setForm();
    }
  }

  setForm(): void {
    const session = this.currentUserSesion();
    if (!this.userForm.value.displayName && session) {
      this.userForm.get('displayName')?.setValue(session.subjectMessageableEntity?.displayName);
    }
    this.profileImageURL.set(session?.subjectMessageableEntity?.profileImageURL ?? '');
  }

  close(): void {
    this.dialogRef.close();
  }

  changePhoto(): void {
    this.imgProfileUpload()?.nativeElement.click();
  }

  onChange(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) {
      this.setFileInAttachments(file);
    }
  }

  setFileInAttachments(file: File): void {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (loadedEvent) => {
      const result = loadedEvent.target?.result;
      if (typeof result === 'string') {
        this.profileImageURL.set(result);
        const uidAux = new Date().toISOString();
        this.attachments.set([
          {
            title: file.name,
            type: AttachmentTypes.IMAGE,
            thumbnailURL: result,
            uid: uidAux,
            file,
          },
        ]);
      }
    };
  }

  save(): void {
    this.store.dispatch(
      updateUser({ displayName: this.userForm.value.displayName as string, attachments: this.attachments() })
    );
    this.close();
  }
}
